import { useState, useMemo, useEffect } from 'react';
import {
  Button, IconButton, Icon, Badge, Sheet, SegmentedControl, useWindowSize, breakpoints,
} from '@openedx/paragon';
import { RiHeart3Line, RiHeart3Fill, RiCheckLine } from '@remixicon/react';
import { useFormatCurrency } from '@blend-ed/blendxplatform/hooks';
import { useIntl } from '@edx/frontend-platform/i18n';

import useRegionalPricing from '../../../../hooks/useRegionalPricing';
import RegionalPriceLabel from '../../components/RegionalPriceLabel';
import { isAbove, isPaidMode } from '../modeTiers';
import messages from './messages';

const formatPrice = (price, formatCurrency, freeLabel) => {
  const amount = Number(price);
  if (!Number.isFinite(amount) || amount <= 0) {
    return freeLabel;
  }
  return formatCurrency(amount, {
    maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
  });
};

const modeLabel = (mode) => mode.modeDisplayName || mode.modeSlug;

const PurchasePanel = ({
  contentId,
  imageUrl,
  title,
  modes,
  modeDetails,
  price,
  isInCart,
  isInWishlist,
  isEnrolled,
  enrolledMode,
  certificateModes = [],
  cartMode,
  onToggleCart,
  onToggleWishlist,
  onChangeMode,
  onEnrollNow,
  pendingTarget,
  isNew,
  paymentsEnabled,
  cartEnabled = true,
  subscriptionPlans = [],
  isSubscribed = false,
  subscriptionPending = false,
  onSubscribe,
  subscriptionOnly = false,
  inviteOnly = false,
}) => {
  const { formatMessage } = useIntl();
  const formatCurrency = useFormatCurrency();
  // Regional (PPP) pricing. Applied to every ONE-TIME price on this panel so
  // the mode list, the headline and the button cannot disagree with each
  // other -- or with what checkout charges. Deliberately NOT applied to the
  // subscription plan below: subscriptions are out of scope for PPP.
  const { priceFor } = useRegionalPricing();
  const cartPending = pendingTarget === 'cart';
  const wishlistPending = pendingTarget === 'wishlist';
  const anyPending = Boolean(pendingTarget);
  const freeLabel = formatMessage(messages.freeMode);

  const sortedModes = useMemo(
    () => [...(modes || [])].sort((a, b) => (a.minPrice || 0) - (b.minPrice || 0)),
    [modes],
  );

  // Tracks that can actually be bought right now. A track the author hid is
  // refused by checkout (`is_course_mode_purchasable` in blendxapi) and an
  // expired one has closed, so offering either only produces a failure at the
  // gateway.
  //
  // Hidden means an upsell row explicitly says `visible: false` — a track with
  // no row at all stays offered. That is blendxapi's own rule
  // (`visibility_map.get(slug, True)`), and it is the only reading that gets
  // both edges right: an org that never configured visibility keeps its whole
  // picker, while an org that hid every track sells none of them.
  const offerableModes = useMemo(() => {
    const visibilityBySlug = new Map(
      (modeDetails || []).map((md) => [md.courseMode, md.visible]),
    );
    const now = Date.now();
    return sortedModes
      .filter((m) => visibilityBySlug.get(m.modeSlug) !== false)
      .filter((m) => !m.expirationDatetime || new Date(m.expirationDatetime).getTime() > now);
  }, [sortedModes, modeDetails]);

  const hasModes = offerableModes.length > 0;
  // Every track hidden or closed: the course exists but nothing about it is
  // sellable, so the buy action has to say so rather than enrol with no mode.
  const noOfferableModes = Array.isArray(modes) && modes.length > 0 && !hasModes;

  const certificateSlugs = useMemo(
    () => new Set((certificateModes || []).filter(Boolean)),
    [certificateModes],
  );

  // Self-service gating. invite_only is the stronger rule: staff add learners,
  // so nothing self-service is offered. subscription_only keeps Subscribe but
  // drops the one-time purchase. Both are enforced by a trigger on `cart`; this
  // is just the matching UI.
  const noOneTimePurchase = Boolean(inviteOnly || subscriptionOnly);

  // The track the learner already holds, and what it cost them. Checkout prices
  // an upgrade as (new mode - enrolled mode), so both are needed to quote it.
  // Looked up in the UNFILTERED list on purpose: an author can hide or expire a
  // track after people enrolled on it, and it still has to count towards the
  // credit — blendxapi gates only the mode being BOUGHT on visibility.
  const enrolledModeEntry = useMemo(
    () => (enrolledMode ? sortedModes.find((m) => m.modeSlug === enrolledMode) : null),
    [sortedModes, enrolledMode],
  );
  const enrolledPrice = Number(enrolledModeEntry?.minPrice) || 0;
  const enrolledModeName = enrolledModeEntry ? modeLabel(enrolledModeEntry) : enrolledMode;

  // Tracks an enrolled learner can move up to. The author's explicit upsell
  // list wins; with no row configured, fall back to every paid track above
  // theirs in tier order — same rule as the learning MFE's upgrade card, so a
  // tenant that never populated upsell overrides still gets the offer.
  // Drawn from the offerable set, so hidden and expired tracks are already out.
  const upgradeModes = useMemo(() => {
    if (!isEnrolled || !enrolledMode) { return []; }
    const explicit = (modeDetails || [])
      .find((md) => md.courseMode === enrolledMode)?.upsellableCourseModes;
    const explicitSlugs = Array.isArray(explicit) ? explicit.filter(Boolean) : [];
    return offerableModes
      .filter((m) => m.modeSlug !== enrolledMode)
      .filter((m) => (explicitSlugs.length > 0
        ? explicitSlugs.includes(m.modeSlug)
        : isPaidMode(m.modeSlug) && isAbove(m.modeSlug, enrolledMode)));
  }, [isEnrolled, enrolledMode, modeDetails, offerableModes]);

  // An upgrade is a one-time purchase, so it's off the table wherever one-time
  // purchases are (invite-only, subscription-only) or payments are off.
  const isUpgrading = Boolean(
    isEnrolled && upgradeModes.length > 0 && !noOneTimePurchase && paymentsEnabled,
  );

  // For a subscription-only product, a track with no plan is a dead end: picking
  // it yields no activeSubPlan, so Subscribe disappears and there's no way to
  // enroll at all. Only offer tracks that actually have a plan.
  const planModeSlugs = useMemo(
    () => new Set((subscriptionPlans || []).map((p) => p.mode).filter(Boolean)),
    [subscriptionPlans],
  );
  const selectableModes = useMemo(() => {
    if (isUpgrading) { return upgradeModes; }
    return subscriptionOnly
      ? offerableModes.filter((m) => planModeSlugs.has(m.modeSlug))
      : offerableModes;
  }, [isUpgrading, upgradeModes, offerableModes, subscriptionOnly, planModeSlugs]);

  const [selectedSlug, setSelectedSlug] = useState(
    cartMode || offerableModes[offerableModes.length - 1]?.modeSlug || null,
  );

  useEffect(() => {
    if (cartMode) {
      setSelectedSlug(cartMode);
    }
  }, [cartMode]);

  // Keep the selection inside the offered set — filtering can strip the track
  // that was selected (or seeded from the cart).
  useEffect(() => {
    if (selectableModes.length === 0) { return; }
    if (!selectableModes.some((m) => m.modeSlug === selectedSlug)) {
      const fallback = selectableModes[selectableModes.length - 1].modeSlug;
      setSelectedSlug(fallback);
      // A cart row can outlive the mode it names — add to cart, then get
      // enrolled into that very track by staff, and the row now points at
      // something we no longer offer. Left alone it would check out at a price
      // this panel never displayed, so drag the cart to the visible selection.
      if (isInCart && cartMode && cartMode !== fallback) {
        onChangeMode?.(fallback);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectableModes, selectedSlug, isInCart, cartMode]);

  const selectedMode = selectableModes.find((m) => m.modeSlug === selectedSlug)
    || selectableModes[0];

  // Subscription plans scoped to the selected enrollment track. For products
  // without modes (programs) the plans all share one mode, so use them all.
  const subModeSlug = hasModes
    ? selectedMode?.modeSlug
    : (subscriptionPlans[0]?.mode || null);
  const modeSubPlans = useMemo(
    () => (subscriptionPlans || [])
      .filter((p) => !subModeSlug || p.mode === subModeSlug)
      .sort((a, b) => (a.amount || 0) - (b.amount || 0)),
    [subscriptionPlans, subModeSlug],
  );
  const [subInterval, setSubInterval] = useState(null);
  const effectiveSubInterval = subInterval
    || modeSubPlans.find((p) => p.interval === 'month')?.interval
    || modeSubPlans[0]?.interval
    || null;
  const activeSubPlan = modeSubPlans.find((p) => p.interval === effectiveSubInterval)
    || modeSubPlans[0]
    || null;
  // Reset the chosen interval when the track changes so it stays valid.
  useEffect(() => {
    setSubInterval(null);
  }, [subModeSlug]);

  // What the yearly plan saves against paying monthly for a year. 0 when there's
  // no saving (or no pair to compare), which hides the badge.
  const yearlySavingsPercent = useMemo(() => {
    const monthly = modeSubPlans.find((p) => p.interval === 'month')?.amount;
    const yearly = modeSubPlans.find((p) => p.interval === 'year')?.amount;
    if (!monthly || !yearly) { return 0; }
    const fullYear = monthly * 12;
    if (yearly >= fullYear) { return 0; }
    return Math.round(((fullYear - yearly) / fullYear) * 100);
  }, [modeSubPlans]);

  const descriptionsMap = useMemo(() => {
    const map = {};
    (modeDetails || []).forEach((md) => {
      let items;
      if (Array.isArray(md.description)) {
        items = md.description;
      } else if (typeof md.description === 'string') {
        items = [md.description];
      } else {
        items = [];
      }
      const cleaned = items
        .filter((s) => typeof s === 'string' && s.trim().length > 0)
        .map((s) => s.trim());
      if (cleaned.length === 0) { return; }
      map[md.courseMode] = {
        summary: cleaned[0],
        bullets: cleaned.slice(1),
      };
    });
    return map;
  }, [modeDetails]);

  const selectedDescription = descriptionsMap[selectedMode?.modeSlug];
  const selectedSummary = selectedDescription?.summary;
  const selectedBullets = selectedDescription?.bullets || [];

  // Checkout charges max(0, new mode - enrolled mode) and only then applies the
  // regional discount (blendxapi payment/*/views.py: the upsell delta is
  // computed before `apply_to_item_prices`). Quote the delta the same way round
  // or the panel and the charge disagree.
  const priceForMode = (mode) => {
    const listPrice = Number(mode?.minPrice) || 0;
    return isUpgrading ? Math.max(0, listPrice - enrolledPrice) : listPrice;
  };

  const displayPrice = hasModes ? priceForMode(selectedMode) : price;
  const cartModeArg = hasModes ? selectedMode?.modeSlug : undefined;

  // On phones the enrollment-plan picker isn't shown inline; it opens as a bottom
  // sheet from the "Add to cart" action so plan choice is part of that flow.
  const { width } = useWindowSize();
  const isMobile = typeof width === 'number' && width < breakpoints.small.minWidth;
  const [isPlanSheetOpen, setPlanSheetOpen] = useState(false);
  const canSubscribe = !inviteOnly;
  const subscribeVisible = Boolean(!isSubscribed && canSubscribe && activeSubPlan && !isEnrolled);
  // With no one-time option there's nothing to buy alongside, so the wishlist
  // moves next to Subscribe rather than sitting alone in an empty footer.
  const wishlistInSubscribe = Boolean(subscriptionOnly && !inviteOnly && subscribeVisible);
  // Cart flow = add-to-cart + drawer. When payments are on but the cart feature
  // is off, the same primary button buys/enrols directly instead.
  const useCartFlow = paymentsEnabled && cartEnabled;
  const usePlanSheet = isMobile && paymentsEnabled && hasModes && !noOneTimePurchase
    && (!isEnrolled || isUpgrading) && selectableModes.length > 1
    && (!useCartFlow || !isInCart);

  const handleModePick = (slug) => {
    setSelectedSlug(slug);
    if (isInCart) {
      onChangeMode?.(slug);
    }
  };

  const cartLabel = isInCart
    ? formatMessage(messages.removeFromCart)
    : formatMessage(messages.addToCart);
  // Direct (no-cart) flow: free content reads "Enroll Now", paid reads "Buy Now".
  const isFree = !(Number(displayPrice) > 0);
  const directLabel = (paymentsEnabled && !isFree)
    ? formatMessage(messages.buyNow)
    : formatMessage(messages.enrollNow);
  let primaryLabel;
  if (noOfferableModes) {
    // Nothing sellable left — say so rather than enrol with no mode at all.
    primaryLabel = formatMessage(messages.enrollmentUnavailable);
  } else if (isUpgrading) {
    // The upgrade still travels through the cart when the cart is on, so keep
    // the cart's own wording there; only the direct flow needs "Upgrade Now".
    primaryLabel = useCartFlow ? cartLabel : formatMessage(messages.upgradeNow);
  } else if (isEnrolled) {
    primaryLabel = formatMessage(messages.alreadyEnrolled);
  } else if (useCartFlow) {
    primaryLabel = cartLabel;
  } else {
    primaryLabel = directLabel;
  }
  const handlePrimaryClick = () => {
    if (usePlanSheet) {
      setPlanSheetOpen(true);
      return;
    }
    if (useCartFlow) {
      onToggleCart?.(contentId, cartModeArg);
    } else {
      onEnrollNow?.(contentId, cartModeArg);
    }
  };
  const handleConfirmFromSheet = () => {
    if (useCartFlow) {
      onToggleCart?.(contentId, selectedMode?.modeSlug);
    } else {
      onEnrollNow?.(contentId, selectedMode?.modeSlug);
    }
    setPlanSheetOpen(false);
  };

  // Rendered either in the purchase footer next to Buy, or beside Subscribe when
  // that's the only action (see wishlistInSubscribe).
  const wishlistButton = (
    <IconButton
      src={isInWishlist ? RiHeart3Fill : RiHeart3Line}
      iconAs={Icon}
      alt={
        isInWishlist
          ? formatMessage(messages.removeFromWishlist)
          : formatMessage(messages.addToWishlist)
      }
      variant="soft-secondary"
      className={`course-detail__purchase-wishlist${
        isInWishlist ? ' course-detail__purchase-wishlist--active' : ''
      }`}
      onClick={() => onToggleWishlist?.(contentId)}
      disabled={isEnrolled || wishlistPending}
      size="lg"
    />
  );

  // Tells an enrolled learner which track they hold — the anchor the upgrade
  // list is priced against, and the only plan information they get when there
  // is nothing above them to buy.
  const currentPlanLine = isEnrolled && enrolledModeName ? (
    <div className="course-detail__purchase-current-plan">
      <RiCheckLine size={14} aria-hidden />
      <span>{formatMessage(messages.currentPlan, { mode: enrolledModeName })}</span>
    </div>
  ) : null;

  // A picker is only worth showing to an enrolled learner when it can actually
  // do something — i.e. when there's an upgrade to select. Otherwise they get
  // the current-plan line alone.
  const showModeList = selectableModes.length > 0 && (!isEnrolled || isUpgrading);

  // The list of tracks the learner can act on right now: every enrollment plan
  // before they enroll, only the upgrade targets afterwards.
  const modeListBlock = (
    <>
      <span className="course-detail__purchase-modes-label">
        {formatMessage(isUpgrading ? messages.upgradeModeLabel : messages.chooseModeLabel)}
      </span>
      <ul className="course-detail__purchase-modes-list">
        {selectableModes.map((mode) => {
          const active = mode.modeSlug === selectedMode.modeSlug;
          return (
            <li key={mode.modeSlug}>
              <button
                type="button"
                className={`course-detail__purchase-mode-row${
                  active ? ' course-detail__purchase-mode-row--active' : ''
                }`}
                onClick={() => handleModePick(mode.modeSlug)}
                disabled={anyPending}
                aria-pressed={active}
              >
                <span
                  className={`course-detail__purchase-radio${
                    active ? ' course-detail__purchase-radio--checked' : ''
                  }`}
                  aria-hidden
                >
                  {active && <RiCheckLine size={12} />}
                </span>
                <span className="course-detail__purchase-mode-name">
                  {modeLabel(mode)}
                </span>
                {/* The only signal for "which plan gets me the certificate" —
                    it is configured per track in authoring. */}
                {certificateSlugs.has(mode.modeSlug) && (
                  <Badge
                    variant="soft"
                    color="success"
                    size="sm"
                    className="course-detail__purchase-mode-cert"
                  >
                    {formatMessage(messages.certificateBadge)}
                  </Badge>
                )}
                {paymentsEnabled && !noOneTimePurchase && (
                  <span className="course-detail__purchase-mode-price">
                    {formatPrice(priceFor(priceForMode(mode)).price, formatCurrency, freeLabel)}
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
      {/* Only worth saying when something was actually deducted — upgrading off
          a free track shows the full price and needs no explanation. */}
      {isUpgrading && enrolledPrice > 0 && (
        <p className="course-detail__purchase-upgrade-hint">
          {formatMessage(messages.upgradePriceHint, { mode: enrolledModeName })}
        </p>
      )}
      {(selectedSummary || selectedBullets.length > 0) && (
        <div className="course-detail__purchase-perks">
          {selectedSummary && (
            <p className="course-detail__purchase-description">
              {selectedSummary}
            </p>
          )}
          {selectedBullets.length > 0 && (
            <ul className="course-detail__purchase-bullets">
              {selectedBullets.map((bullet, idx) => (
                // eslint-disable-next-line react/no-array-index-key
                <li key={`${selectedMode?.modeSlug}-${idx}`}>
                  {bullet}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </>
  );

  // The enrollment-plan picker — rendered inline on desktop, or inside the bottom
  // sheet on phones (so it's part of the add-to-cart flow). Hidden for invite-only
  // products, where picking a track drives nothing; kept for subscription-only,
  // where it still selects which track's plans are offered.
  const modesSection = hasModes && !inviteOnly && (showModeList || currentPlanLine) ? (
    <div className="course-detail__purchase-modes">
      {currentPlanLine}
      {showModeList && modeListBlock}
    </div>
  ) : null;

  return (
    <aside className="course-detail__purchase">
      <div
        className="course-detail__purchase-image"
        role="img"
        aria-label={title}
        style={{ backgroundImage: `url(${imageUrl})` }}
      >
        {isNew && (
          <div className="course-detail__purchase-image-new-badge">
            <Badge variant="soft" color="success" size="sm">
              {formatMessage(messages.newBadge)}
            </Badge>
          </div>
        )}
      </div>

      <div className="course-detail__purchase-body">
        {/* Inline on desktop; on phones it normally moves into the add-to-cart
            bottom sheet — but that sheet doesn't exist without a one-time
            purchase, so render inline there too or the picker would vanish.
            Enrolled learners with no sheet to open (nothing to upgrade to) keep
            their current-plan line inline for the same reason. */}
        {(!isMobile || noOneTimePurchase || (isEnrolled && !usePlanSheet)) && modesSection}

        {/* Subscribed learners see only the Subscribed button (below). The footer
            is skipped entirely once it would be empty — i.e. subscription-only,
            where the price and Buy are gone and the wishlist has moved next to
            Subscribe. */}
        {!isSubscribed && !wishlistInSubscribe && (
          <div className="course-detail__purchase-footer">
            {paymentsEnabled && !noOneTimePurchase && (
              <div className="course-detail__purchase-price">
                <RegionalPriceLabel price={displayPrice} />
              </div>
            )}
            {inviteOnly && !isEnrolled && (
              <div className="course-detail__purchase-gated">
                {formatMessage(messages.inviteOnlyNotice)}
              </div>
            )}
            <div className="course-detail__purchase-actions">
              {/* Buy/Add-to-cart is dropped entirely when the product isn't
                  self-service, but an enrolled learner still gets their status. */}
              {(!noOneTimePurchase || isEnrolled) && (
              <Button
                variant="primary"
                className="course-detail__purchase-cart"
                onClick={handlePrimaryClick}
                disabled={(isEnrolled && !isUpgrading) || noOfferableModes || cartPending}
                size='lg'
              >
                {primaryLabel}
              </Button>
              )}
              {!wishlistInSubscribe && wishlistButton}
            </div>
          </div>
        )}

        {isSubscribed && (
          <div className="course-detail__purchase-subscription d-flex flex-column gap-2 mt-3">
            <Badge
              variant="soft"
              color="success"
              size="lg"
              className="course-detail__purchase-subscribed d-flex align-items-center justify-content-center w-100"
            >
              {formatMessage(messages.subscribed)}
            </Badge>
          </div>
        )}

        {/* Subscribe is an alternative to buying a paid track once — and the only
            option at all for a subscription-only product. Deliberately NOT gated on
            a one-time price existing: that guard made Subscribe unreachable for any
            product without one, which is exactly the subscription-only case. */}
        {subscribeVisible && (
          <div className="course-detail__purchase-subscription d-flex flex-column gap-2 mt-3">
            {/* "Or subscribe" only makes sense against a one-time alternative;
                for subscription-only there is nothing to be an alternative to. */}
            {!subscriptionOnly && (
              <div className="course-detail__purchase-suboffer">
                {formatMessage(messages.orSubscribe)}
              </div>
            )}
            {/* Paragon's SegmentedControl — the usual single-row billing toggle,
                already styled and already radio-semantic (radiogroup/radio). The
                price lives on the Subscribe button below, so this only picks the
                interval: no label, no per-row price. */}
            {modeSubPlans.length > 1 && (
              <SegmentedControl
                size="sm"
                value={effectiveSubInterval}
                onChange={setSubInterval}
                disabled={subscriptionPending}
                aria-label={formatMessage(messages.chooseIntervalLabel)}
              >
                {modeSubPlans.map((p) => (
                  <SegmentedControl.Item key={p.interval} value={p.interval}>
                    {formatMessage(
                      p.interval === 'year' ? messages.intervalYearly : messages.intervalMonthly,
                    )}
                    {p.interval === 'year' && yearlySavingsPercent > 0 && (
                      <Badge variant="soft" color="success" size="sm" className="ml-2">
                        {formatMessage(messages.intervalSave, { percent: yearlySavingsPercent })}
                      </Badge>
                    )}
                  </SegmentedControl.Item>
                ))}
              </SegmentedControl>
            )}
            <div className="course-detail__purchase-actions">
              <Button
                variant="soft-primary"
                size="lg"
                className="flex-fill"
                onClick={() => onSubscribe?.(subModeSlug, activeSubPlan.interval)}
                disabled={subscriptionPending}
              >
                {formatMessage(messages.subscribeCta, {
                  price: formatPrice(activeSubPlan.amount / 100, formatCurrency, ''),
                  interval: formatMessage(
                    activeSubPlan.interval === 'year' ? messages.perYear : messages.perMonth,
                  ),
                })}
              </Button>
              {wishlistInSubscribe && wishlistButton}
            </div>
          </div>
        )}
      </div>

      {/* Mobile: tapping "Add to cart" opens the plan picker here, then confirms.
          Never reachable when the product isn't self-service — its confirm button
          goes straight to cart/enroll. */}
      {isMobile && hasModes && !noOneTimePurchase && (
        <Sheet
          position="bottom"
          show={isPlanSheetOpen}
          onClose={() => setPlanSheetOpen(false)}
          className="course-detail__plan-sheet"
        >
          {modesSection}
          <Button
            variant="primary"
            block
            size="lg"
            className="course-detail__plan-sheet-confirm"
            onClick={handleConfirmFromSheet}
            disabled={cartPending}
          >
            {(() => {
              if (useCartFlow) { return formatMessage(messages.addToCart); }
              return isUpgrading ? formatMessage(messages.upgradeNow) : directLabel;
            })()}
            {paymentsEnabled
              && ` · ${formatPrice(priceFor(displayPrice).price, formatCurrency, freeLabel)}`}
          </Button>
        </Sheet>
      )}
    </aside>
  );
};

export default PurchasePanel;
